import React, { useEffect, useState } from "react";
import { Card as RawCard } from "@/components/ui/card";
import { Button as RawButton } from "@/components/ui/button";
import { Input as RawInput } from "@/components/ui/input";
import { Label as RawLable } from "@/components/ui/lable";
import {
  Loader2,
  AlertCircle,
  UserPlus,
  Users,
  Trash2,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  getSavedGolfers,
  saveGolfer,
  deleteGolfer,
} from "../../services/golferservice";

const emptyForm = { name: "", passportNumber: "", phone: "", email: "" };

function validate(g) {
  const errors = {};
  if (!g.name?.trim()) errors.name = "Name is required.";
  if (!g.passportNumber?.trim())
    errors.passportNumber = "Passport number is required.";
  if (!g.phone?.trim()) errors.phone = "Phone number is required.";
  if (!g.email?.trim()) errors.email = "Email is required.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.email.trim()))
    errors.email = "Enter a valid email address.";
  return errors;
}

/**
 * Profile > Golfing Buddies tab. Shows every golfer the lead golfer has
 * saved for reuse on future bookings (the same "golfers" collection that
 * powers the Golfing Buddies card on the "Who's playing?" step), with the
 * ability to add or remove one directly from the account page.
 *
 * @param {{ uid?: string }} props
 */
export default function GolfingBuddiesSection({ uid }) {
  const [buddies, setBuddies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Label = /** @type {React.ComponentType<any>} */ (RawLable);
  const Input = /** @type {React.ComponentType<any>} */ (RawInput);

  const fetchBuddies = async () => {
    if (!uid) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getSavedGolfers(uid);
      setBuddies(list);
    } catch (err) {
      console.error("Failed to load golfing buddies:", err);
      setError(err?.message || "Failed to load your golfing buddies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuddies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const handleAdd = async () => {
    const errors = validate(form);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      await saveGolfer(uid, {
        name: form.name.trim(),
        passportNumber: form.passportNumber.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      });
      setForm(emptyForm);
      setFormErrors({});
      setShowAddForm(false);
      fetchBuddies();
    } catch (err) {
      console.error("Failed to add golfing buddy:", err);
      setFormErrors({
        _global: err?.message || "Failed to add this golfer. Try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    setRemovingId(id);
    try {
      await deleteGolfer(id);
      setBuddies((prev) => prev.filter((g) => g.id !== id));
    } catch (err) {
      console.error("Failed to remove golfing buddy:", err);
      setError(err?.message || "Failed to remove this golfer. Try again.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-6 bg-white border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#0B2421] flex items-center gap-2">
            <Users className="h-6 w-6" />
            Golfing Buddies
          </h2>
          {!showAddForm && (
            <Button
              onClick={() => setShowAddForm(true)}
              className="h-9 px-3 text-white bg-[#0B2421] hover:bg-[#0B2421]/85"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Buddy
            </Button>
          )}
        </div>

        <p className="mb-6 text-gray-500">
          Golfers you've added before — select them again on future bookings
          instead of retyping their details.
        </p>

        {showAddForm && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-[#0B2421]">
                Add a Golfing Buddy
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setForm(emptyForm);
                  setFormErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Full Name
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  placeholder="As shown on passport"
                  className="h-11 text-gray-700"
                />
                {formErrors.name && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.name}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Passport Number
                </Label>
                <Input
                  value={form.passportNumber}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      passportNumber: e.target.value,
                    }))
                  }
                  placeholder="e.g. K1234567"
                  className="h-11 text-gray-700"
                />
                {formErrors.passportNumber && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.passportNumber}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Phone Number
                </Label>
                <Input
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: e.target.value }))
                  }
                  placeholder="+65 9123 4567"
                  className="h-11 text-gray-700"
                />
                {formErrors.phone && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.phone}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Email Address
                </Label>
                <Input
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  placeholder="name@example.com"
                  className="h-11 text-gray-700"
                />
                {formErrors.email && (
                  <p className="text-xs text-red-500 mt-1">
                    {formErrors.email}
                  </p>
                )}
              </div>
            </div>
            {formErrors._global && (
              <p className="text-xs text-red-500 mt-3">
                {formErrors._global}
              </p>
            )}
            <Button
              onClick={handleAdd}
              disabled={saving}
              className="mt-4 h-10 px-4 text-white bg-[#0B2421] hover:bg-[#0B2421]/85"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Buddy
            </Button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading your golfing buddies…
          </div>
        ) : error ? (
          <p className="text-sm text-red-600 flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4" /> {error}
          </p>
        ) : buddies.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">
            You haven't added any golfing buddies yet — they'll also appear
            here automatically when you save someone during a booking.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {buddies.map((g) => (
              <div
                key={g.id}
                className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4"
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {g.name}
                  </p>
                  <p className="text-xs text-gray-600 truncate">
                    Passport {g.passportNumber} · {g.phone}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{g.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(g.id)}
                  disabled={removingId === g.id}
                  className="text-gray-400 hover:text-red-500 disabled:opacity-40 shrink-0 ml-3"
                  aria-label={`Remove ${g.name}`}
                >
                  {removingId === g.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </motion.div>
  );
}
